import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FiArrowLeft, FiChevronDown, FiChevronUp } from "react-icons/fi";
import Skeleton from "../Loader/Skeleton";
import PaymentMethodSelector from "../shared/PaymentMethodSelector";
import * as apicall from "../Apicall";
import {
  getSubscriptionPlanDetail,
  getPlanSubscribers,
  getUserVisits,
  getSubscriptionOrders,
  recordSubscriptionPayment,
} from "../../services/subscriptionService";
import {
  deriveScheduleMessage as buildScheduleMessage,
  getUpcomingDates,
} from "../../utils/scheduleDateMath";

// ── Schedule helpers ────────────────────────────────────────────────────────
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
// ────────────────────────────────────────────────────────────────────────────

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  // Parse date parts directly to avoid UTC-to-local timezone shift
  const parts = String(dateStr).split("T")[0].split("-");
  if (parts.length === 3) {
    const [year, month, day] = parts;
    return `${parseInt(day, 10)} ${MONTH_NAMES[parseInt(month, 10) - 1]} ${year}`;
  }
  const d = new Date(dateStr);
  return `${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
};

const formatTime = (timeStr) => {
  if (!timeStr) return "-";
  // API may already include AM/PM — return as-is to avoid double-appending
  if (/am|pm/i.test(timeStr)) return timeStr;
  const [h, m] = timeStr.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${m} ${ampm}`;
};

const StatusBadge = ({ status }) => {
  const isActive = status === "Active";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 12px",
        borderRadius: "9999px",
        fontSize: "12px",
        fontWeight: "600",
        background: isActive ? "#E6F9F0" : "#FFF3F3",
        color: isActive ? "#27AE60" : "#C0335A",
        border: `1px solid ${isActive ? "#27AE60" : "#C0335A"}`,
      }}
    >
      {status}
    </span>
  );
};

const BillingStatusBadge = ({ status }) => {
  if (!status) return <span style={{ color: "#9CA3AF", fontSize: "13px" }}>-</span>;
  const isActive = status === "Active";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 12px",
        borderRadius: "9999px",
        fontSize: "12px",
        fontWeight: "600",
        background: isActive ? "#E6F9F0" : "#FFF3F3",
        color: isActive ? "#27AE60" : "#C0335A",
        border: `1px solid ${isActive ? "#27AE60" : "#C0335A"}`,
      }}
    >
      {status}
    </span>
  );
};

// Modal for recording a subsequent month's recurring payment for a subscriber
const RecordPaymentModal = ({ user, paymentId, onClose, onSuccess }) => {
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentStatusOptions, setPaymentStatusOptions] = useState([]);
  const [cashPaymentStatus, setCashPaymentStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await apicall.get("get_ezp_payment_status");
        if (res?.success && res.data?.length) {
          setPaymentStatusOptions(res.data);
          const cashPaid = res.data.find(p => (p.name || "").toLowerCase() === "cash paid");
          const pending = res.data.find(p => (p.name || "").toLowerCase() === "pending");
          setCashPaymentStatus(cashPaid ? cashPaid.id : pending ? pending.id : res.data[0].id);
        }
      } catch {
        // cosmetic dropdown only — don't block the modal
      }
    })();
  }, []);

  const userSubscriptionId = user.id || user.user_subscription_id;

  const handleConfirm = async () => {
    try {
      setSubmitting(true);
      await recordSubscriptionPayment({
        user_subscription_id: userSubscriptionId,
        payment_id: paymentId || undefined,
        payment_method: paymentMethod,
        payment_status_id: paymentMethod === "cash" ? cashPaymentStatus : undefined,
      });
      toast.success("Payment recorded successfully");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err?.message || "Failed to record payment");
    } finally {
      setSubmitting(false);
    }
  };

  // Rendered from inside a <tbody> row — a fixed-position modal can't be a
  // direct DOM child of a table, so portal it to document.body (same
  // pattern as ConfirmationModal.jsx / FollowUpConfirmModal.jsx).
  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.5)", zIndex: 2000,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 16, padding: "24px 26px", width: "100%", maxWidth: 380, boxShadow: "0 8px 30px rgba(0,0,0,0.2)" }}
      >
        <div style={{ fontWeight: 700, fontSize: 16, color: "#111827", marginBottom: 4 }}>Record Payment</div>
        <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 16 }}>
          {user.first_name || user.userName || user.user_name} — payment for the monthly order
        </div>

        <PaymentMethodSelector value={paymentMethod} onChange={setPaymentMethod} />

        {paymentMethod === "cash" && paymentStatusOptions.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 7, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Payment Status
            </label>
            <select
              value={cashPaymentStatus ?? ""}
              onChange={e => setCashPaymentStatus(Number(e.target.value))}
              style={{ width: "100%", padding: "9px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 14, color: "#0f172a", outline: "none", background: "#f8fafc" }}
            >
              {paymentStatusOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button
            type="button"
            onClick={onClose}
            style={{ flex: 1, padding: "10px", background: "#fff", border: "1px solid #D1D5DB", borderRadius: 10, fontSize: 14, fontWeight: 600, color: "#374151", cursor: "pointer" }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            style={{ flex: 1, padding: "10px", background: submitting ? "#e89fb7" : "#C0335A", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, color: "#fff", cursor: submitting ? "not-allowed" : "pointer" }}
          >
            {submitting ? "Recording..." : "Confirm"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const UpcomingVisitsSection = ({ plan }) => {
  if (!plan || plan.subscription_type !== "Fixed") return null;

  const rawFixedType = plan.fixed_type;
  const fixedType = rawFixedType === "HalfYearly" ? "Half Yearly" : rawFixedType;
  const isWeekly = fixedType === "Weekly";

  const scheduleDays = (() => {
    if (!plan.fixed_schedule) return [];
    try {
      const parsed = typeof plan.fixed_schedule === "string" ? JSON.parse(plan.fixed_schedule) : plan.fixed_schedule;
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  })();

  const scheduleMessage = isWeekly
    ? "If the subscription is required daily, just select all days to schedule."
    : buildScheduleMessage(rawFixedType, plan.start_date, scheduleDays);

  const upcomingDates = getUpcomingDates(rawFixedType, plan.start_date, scheduleDays, 10, plan.end_date);

  const thStyle = { padding: "10px 16px", fontWeight: "600", fontSize: "12px", color: "#374151", textAlign: "left", borderBottom: "1px solid #E5E7EB", whiteSpace: "nowrap" };

  return (
    <div style={{ background: "#fff", borderRadius: "12px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: "24px", overflow: "hidden" }}>
      <div style={{ padding: "20px 24px", borderBottom: "1px solid #F3F4F6" }}>
        <div style={{ fontSize: "16px", fontWeight: "700", color: "#111827" }}>Upcoming Visits</div>
        <div style={{ fontSize: "13px", color: "#6B7280", marginTop: "2px" }}>
          Next scheduled visits based on {fixedType || "plan"} frequency
          {plan.end_date ? ` · limited to plan end date (${formatDate(plan.end_date)})` : ""}
        </div>
      </div>

      <div style={{ padding: "16px 24px" }}>
        {scheduleMessage && (
          <div style={{
            marginBottom: upcomingDates.length > 0 ? "16px" : "0",
            padding: "10px 14px",
            background: isWeekly ? "#EFF6FF" : "#F0FDF4",
            border: `1px solid ${isWeekly ? "#BFDBFE" : "#BBF7D0"}`,
            borderRadius: "8px",
            fontSize: "13px",
            color: isWeekly ? "#1D4ED8" : "#15803D",
          }}>
            {scheduleMessage}
          </div>
        )}

        {upcomingDates.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#F9FAFB" }}>
                  <th style={{ ...thStyle, width: "48px" }}>#</th>
                  <th style={thStyle}>Day</th>
                  <th style={thStyle}>Date</th>
                  {plan.fixed_schedule_time && <th style={thStyle}>Time Slot</th>}
                </tr>
              </thead>
              <tbody>
                {upcomingDates.map((d, i) => (
                  <tr
                    key={i}
                    style={{ borderBottom: "1px solid #F3F4F6" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <td style={{ padding: "12px 16px", fontSize: "13px", color: "#9CA3AF", fontWeight: "600" }}>{i + 1}</td>
                    <td style={{ padding: "12px 16px", fontSize: "13px" }}>
                      <span style={{ display: "inline-block", background: "#FDE8EF", color: "#C0335A", borderRadius: "6px", padding: "2px 8px", fontWeight: "600", fontSize: "12px" }}>
                        {WEEKDAY_SHORT[d.getDay()]}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: "13px", color: "#374151", fontWeight: "500" }}>
                      {d.getDate()} {MONTH_NAMES[d.getMonth()]} {d.getFullYear()}
                    </td>
                    {plan.fixed_schedule_time && (
                      <td style={{ padding: "12px 16px", fontSize: "13px", color: "#6B7280" }}>{plan.fixed_schedule_time}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : !isWeekly && (
          <div style={{ color: "#6B7280", fontSize: "14px", padding: "8px 0" }}>
            No upcoming visits could be derived. Ensure the plan has a start date and schedule days configured.
          </div>
        )}
      </div>
    </div>
  );
};

// Expandable visit history row per user
const VISIT_COLS = [
  { label: "Booking ID",    key: "display_booking_id" },
  { label: "Service Date",  key: "service_date" },
  { label: "Service Time",  key: "service_time" },
  { label: "Status",        key: "name" },
  { label: "Payment",       key: "payment_status" },
];

const UserVisitsRow = ({ user, onPaymentRecorded }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [sortCol, setSortCol] = useState("service_date");
  const [sortDir, setSortDir] = useState("asc");
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [recordOrderId, setRecordOrderId] = useState(null);
  const [orders, setOrders] = useState([]);

  // user.id is the ezp_user_subscriptions.id (user_subscription_id)
  const userSubscriptionId = user.id || user.user_subscription_id;

  const loadVisits = useCallback(async () => {
    if (fetched) return;
    try {
      setLoading(true);
      const res = await getUserVisits(userSubscriptionId);
      setVisits(res.data || res || []);
      setFetched(true);
    } catch {
      toast.error(`Failed to load visits for ${user.first_name || user.userName || user.user_name}`);
    } finally {
      setLoading(false);
    }
  }, [userSubscriptionId, fetched]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadOrders = useCallback(async () => {
    try {
      const res = await getSubscriptionOrders(userSubscriptionId);
      setOrders(res.data || []);
    } catch {
      // orders are an extra view - the visit list below still works without them
    }
  }, [userSubscriptionId]);

  const toggle = () => {
    if (!expanded && !fetched) { loadVisits(); loadOrders(); }
    setExpanded(prev => !prev);
  };

  const handleVisitSort = (key) => {
    setSortCol(prev => {
      if (prev === key) { setSortDir(d => d === "asc" ? "desc" : "asc"); return prev; }
      setSortDir("asc");
      return key;
    });
  };

  const sortedVisits = [...visits].sort((a, b) => {
    const aVal = a[sortCol] ?? "";
    const bVal = b[sortCol] ?? "";
    if (sortCol === "service_date") {
      const ad = aVal ? new Date(aVal) : new Date(0);
      const bd = bVal ? new Date(bVal) : new Date(0);
      return sortDir === "asc" ? ad - bd : bd - ad;
    }
    return sortDir === "asc"
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  const tdBase = {
    padding: "14px 16px",
    fontSize: "14px",
    color: "#374151",
    verticalAlign: "middle",
    borderBottom: expanded ? "none" : "1px solid #F3F4F6",
  };

  const scheduleDays = (() => {
    if (!user.schedule_days) return "-";
    try {
      const parsed = typeof user.schedule_days === "string" ? JSON.parse(user.schedule_days) : user.schedule_days;
      return Array.isArray(parsed) ? parsed.join(", ") : String(user.schedule_days);
    } catch {
      return String(user.schedule_days);
    }
  })();

  return (
    <>
      <tr
        onMouseEnter={e => { if (!expanded) e.currentTarget.style.background = "#F9FAFB"; }}
        onMouseLeave={e => { if (!expanded) e.currentTarget.style.background = "transparent"; }}
        style={{ transition: "background 0.15s", background: expanded ? "#FDF2F5" : "transparent" }}
      >
        <td style={tdBase}>{user.first_name || user.userName || user.user_name || "-"}</td>
        <td style={tdBase}>{formatDate(user.purchase_date || user.startDate || user.start_date)}</td>
        <td style={tdBase}>{formatDate(user.end_date || user.endDate)}</td>
        <td style={{ ...tdBase, textAlign: "center" }}>{user.total_visits ?? user.totalVisits ?? "-"}</td>
        <td style={tdBase}>{user.schedule_type || "-"}</td>
        <td style={tdBase}>{user.schedule_frequency || "-"}</td>
        <td style={tdBase}>{formatDate(user.schedule_start_date)}</td>
        <td style={tdBase}>{scheduleDays}</td>
        <td style={tdBase}>{user.schedule_time_slot || "-"}</td>
        <td style={tdBase}>{user.provider_name || "-"}</td>
        <td style={tdBase}><StatusBadge status={user.plan_status || user.status || "Active"} /></td>
        <td style={tdBase}>
          {user.billing_type === "Recurring" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-start" }}>
              <BillingStatusBadge status={user.billing_status} />
              <div style={{ fontSize: "11px", color: "#6B7280" }}>
                Next due: {formatDate(user.next_due_date)}
              </div>
              <button
                type="button"
                onClick={() => { setRecordOrderId(null); setShowRecordPayment(true); }}
                style={{
                  background: "#FDE8EF",
                  border: "1px solid #C0335A",
                  color: "#C0335A",
                  borderRadius: "6px",
                  padding: "3px 10px",
                  fontSize: "11px",
                  fontWeight: "600",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Record Payment
              </button>
            </div>
          ) : (
            <span style={{ color: "#9CA3AF", fontSize: "13px" }}>-</span>
          )}
        </td>
        <td style={tdBase}>
          <button
            onClick={toggle}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              background: expanded ? "#FDE8EF" : "#F3F4F6",
              border: `1px solid ${expanded ? "#C0335A" : "#E5E7EB"}`,
              color: expanded ? "#C0335A" : "#374151",
              borderRadius: "8px",
              padding: "5px 12px",
              fontSize: "12px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.15s",
              whiteSpace: "nowrap",
            }}
          >
            {expanded ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />}
            {expanded ? "Hide" : "Visits"}
          </button>
        </td>
      </tr>

      {/* Expanded visit history */}
      {expanded && (
        <tr>
          <td
            colSpan={13}
            style={{
              padding: "0 0 0 48px",
              background: "#FDF9FA",
              borderBottom: "1px solid #F3F4F6",
            }}
          >
            <div style={{ padding: "16px 16px 16px 0" }}>
              <div style={{ fontSize: "13px", fontWeight: "600", color: "#C0335A", marginBottom: "10px" }}>
                Visit History — {user.userName || user.user_name}
              </div>
              {loading ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {[1, 2, 3].map(i => <Skeleton key={i} height="14px" marginBottom="0" />)}
                </div>
              ) : orders.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  {orders.map(o => {
                    const unpaid = Number(o.payment_status_id) === 1;
                    const failed = Number(o.payment_status_id) === 3;
                    const badge = unpaid
                      ? { bg: "#FEF3C7", fg: "#B45309" }
                      : failed
                        ? { bg: "#FEE2E2", fg: "#B91C1C" }
                        : { bg: "#DCFCE7", fg: "#15803D" };
                    return (
                      <div key={o.id} style={{ border: "1px solid #E5E7EB", borderRadius: "10px", background: "#fff", overflow: "hidden" }}>
                        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "14px", padding: "10px 14px", background: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
                          <span style={{ fontWeight: 700, fontSize: "13px", color: "#111827" }}>{o.order_ref || `Order #${o.id}`}</span>
                          <span style={{ fontSize: "12px", color: "#6B7280" }}>
                            {formatDate(o.billing_period_start)} – {formatDate(o.billing_period_end)}
                          </span>
                          {o.due_date && <span style={{ fontSize: "12px", color: "#6B7280" }}>Due {formatDate(o.due_date)}</span>}
                          <span style={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>
                            {o.currency} {Number(o.amount || 0).toFixed(2)}
                          </span>
                          <span style={{ background: badge.bg, color: badge.fg, borderRadius: "999px", padding: "2px 10px", fontSize: "11px", fontWeight: 700 }}>
                            {o.payment_status || "-"}
                          </span>
                          {unpaid && (
                            <button
                              type="button"
                              onClick={() => { setRecordOrderId(o.id); setShowRecordPayment(true); }}
                              style={{ background: "#FDE8EF", border: "1px solid #C0335A", color: "#C0335A", borderRadius: "6px", padding: "3px 10px", fontSize: "11px", fontWeight: 600, cursor: "pointer", marginLeft: "auto" }}
                            >
                              Mark paid
                            </button>
                          )}
                        </div>
                        {o.visits?.length > 0 ? (
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr style={{ background: "#fff" }}>
                                {["Booking ID", "Service Date", "Service Time", "Status", "Payment"].map(h => (
                                  <th key={h} style={{ padding: "6px 14px", fontWeight: 600, fontSize: "11px", color: "#6B7280", textAlign: "left", borderBottom: "1px solid #F3F4F6" }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {o.visits.map((v, i) => (
                                <tr key={i} style={{ borderBottom: "1px solid #F3F4F6" }}>
                                  <td style={{ padding: "6px 14px", fontSize: "13px" }}>
                                    {v.display_booking_id ? (
                                      <span
                                        onClick={() => navigate(`/vendor_bookings/${v.display_booking_id}`)}
                                        style={{ color: "#C0335A", fontWeight: 600, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: "2px" }}
                                      >
                                        {v.display_booking_id}
                                      </span>
                                    ) : "-"}
                                  </td>
                                  <td style={{ padding: "6px 14px", fontSize: "13px" }}>{formatDate(v.service_date)}</td>
                                  <td style={{ padding: "6px 14px", fontSize: "13px" }}>{formatTime(v.service_time)}</td>
                                  <td style={{ padding: "6px 14px", fontSize: "13px" }}>{v.name || "-"}</td>
                                  <td style={{ padding: "6px 14px", fontSize: "13px" }}>{v.payment_status || "-"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div style={{ padding: "10px 14px", fontSize: "12px", color: "#9CA3AF" }}>No visits in this month yet.</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : visits.length === 0 ? (
                <div style={{ fontSize: "13px", color: "#6B7280", padding: "8px 0" }}>No visits recorded.</div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#F3F4F6" }}>
                        {VISIT_COLS.map(({ label, key }) => {
                          const isActive = sortCol === key;
                          return (
                            <th
                              key={key}
                              onClick={() => handleVisitSort(key)}
                              style={{
                                padding: "8px 12px",
                                fontWeight: "600",
                                fontSize: "12px",
                                color: isActive ? "#C0335A" : "#374151",
                                textAlign: "left",
                                borderBottom: "1px solid #E5E7EB",
                                whiteSpace: "nowrap",
                                cursor: "pointer",
                                userSelect: "none",
                              }}
                            >
                              {label}
                              <span style={{ marginLeft: "4px", opacity: isActive ? 1 : 0.35 }}>
                                {isActive ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
                              </span>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedVisits.map((v, i) => (
                        <tr
                          key={i}
                          style={{ borderBottom: "1px solid #F3F4F6" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        >
                          <td style={{ padding: "8px 12px", fontSize: "13px" }}>
                            {v.display_booking_id ? (
                              <span
                                onClick={() => navigate(`/vendor_bookings/${v.display_booking_id}`)}
                                style={{
                                  color: "#C0335A",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                  textUnderlineOffset: "2px",
                                }}
                              >
                                {v.display_booking_id}
                              </span>
                            ) : "-"}
                          </td>
                          <td style={{ padding: "8px 12px", fontSize: "13px", color: "#374151" }}>
                            {formatDate(v.service_date)}
                          </td>
                          <td style={{ padding: "8px 12px", fontSize: "13px", color: "#374151" }}>
                            {formatTime(v.service_time)}
                          </td>
                          <td style={{ padding: "8px 12px", fontSize: "13px", color: "#374151" }}>
                            {v.name || "-"}
                          </td>
                          <td style={{ padding: "8px 12px", fontSize: "13px", color: "#374151" }}>
                            {v.payment_status || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}

      {showRecordPayment && (
        <RecordPaymentModal
          user={user}
          paymentId={recordOrderId}
          onClose={() => setShowRecordPayment(false)}
          onSuccess={() => { onPaymentRecorded?.(); loadOrders(); }}
        />
      )}
    </>
  );
};

// ────────────────────────────────────────────────────────────────────────────
const SubscriptionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const vendorProfile = JSON.parse(localStorage.getItem("vendorProfile"));
  const isClinicService = Number(vendorProfile?.industry) === 1;
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [subscribers, setSubscribers] = useState([]);
  const [subsLoading, setSubsLoading] = useState(true);

  const fetchSubscribers = useCallback(async () => {
    try {
      setSubsLoading(true);
      const res = await getPlanSubscribers(id);
      setSubscribers(res.data || res || []);
    } catch {
      toast.error("Failed to load subscribers");
    } finally {
      setSubsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        setPlanLoading(true);
        const res = await getSubscriptionPlanDetail(id);
        setPlan(res.data || res);
      } catch {
        toast.error("Failed to load plan details");
      } finally {
        setPlanLoading(false);
      }
    };

    fetchPlan();
    fetchSubscribers();
  }, [id, fetchSubscribers]);

  const p = plan;

  const thStyle = {
    background: "#F9FAFB",
    fontWeight: "600",
    fontSize: "13px",
    color: "#374151",
    padding: "12px 16px",
    borderBottom: "1px solid #E5E7EB",
    textAlign: "left",
    whiteSpace: "nowrap",
  };

  return (
    <div style={{ padding: "24px", background: "#F3F4F6", minHeight: "100vh", fontFamily: "Inter, sans-serif" }}>
      {/* Back */}
      <button
        onClick={() => navigate("/subscription")}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#6B7280",
          fontWeight: "600",
          fontSize: "13px",
          padding: "0",
          marginBottom: "20px",
          letterSpacing: "0.05em",
          textTransform: "uppercase",
        }}
      >
        <FiArrowLeft size={16} />
        Back
      </button>

      {/* Page title */}
      <div style={{ marginBottom: "20px" }}>
        {planLoading ? (
          <Skeleton width="300px" height="32px" marginBottom="0" />
        ) : (
          <h2 style={{ margin: 0, fontSize: "24px", fontWeight: "700", color: "#111827" }}>
            {p?.name || "Plan Details"}
          </h2>
        )}
      </div>

      {/* Plan info card */}
      <div
        style={{
          background: "#FDE8EF",
          borderRadius: "12px",
          padding: "24px",
          marginBottom: "24px",
        }}
      >
        {planLoading ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "16px" }}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => <Skeleton key={i} height="52px" marginBottom="0" />)}
          </div>
        ) : (() => {
          const activeCount = subsLoading
            ? (p?.active_subscriber_count ?? 0)
            : subscribers.filter(u => (u.plan_status || u.status || "Active") === "Active").length;

          const fixedSchedule = (() => {
            if (!p?.fixed_schedule) return null;
            try {
              const parsed = typeof p.fixed_schedule === "string" ? JSON.parse(p.fixed_schedule) : p.fixed_schedule;
              return Array.isArray(parsed) ? parsed.join(", ") : String(p.fixed_schedule);
            } catch {
              return String(p.fixed_schedule);
            }
          })();

          const baseFields = [
            { label: "Plan Type", value: p?.subscription_type || "-" },
            { label: "Service Category", value: p?.service_name || "-" },
            { label: "Sub Category", value: p?.service_type_name || "-" },
            { label: "Service Type", value: p?.service_offering_name || "-" },
            { label: "Pricing", value: `AED ${Number(p?.pricing || 0).toFixed(2)}` },
            { label: "Start Date", value: formatDate(p?.start_date) },
            { label: "End Date", value: formatDate(p?.end_date) },
            { label: "Status", value: p?.status || "-", isStatus: true },
            ...(p?.subscription_type === "Fixed"
              ? [
                  { label: "Frequency", value: p?.fixed_type === "HalfYearly" ? "Half Yearly" : (p?.fixed_type || "-") },
                  ...(fixedSchedule ? [{ label: "Schedule Days", value: fixedSchedule }] : []),
                  ...(p?.fixed_schedule_time ? [{ label: "Schedule Time", value: p.fixed_schedule_time }] : []),
                  ...(p?.crew_name ? [{ label: isClinicService ? "Assigned Doctor" : "Assigned Crew", value: p.crew_name }] : []),
                ]
              : [
                  { label: "Sessions", value: p?.number_of_sessions || "-" },
                  ...(p?.number_of_sessions === "Limited" && p?.total_sessions
                    ? [{ label: "Total Sessions", value: p.total_sessions }]
                    : []),
                ]),
            { label: "Active Subscribers", value: activeCount },
          ];

          return (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "16px" }}>
              {baseFields.map(item => (
                <div key={item.label} style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "12px", color: "#C0335A", fontWeight: "600", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    {item.label}
                  </div>
                  {item.isStatus ? (
                    <StatusBadge status={item.value} />
                  ) : (
                    <div style={{ fontSize: "18px", fontWeight: "700", color: "#111827" }}>
                      {item.value}
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Upcoming visits */}
      {!planLoading && <UpcomingVisitsSection plan={p} />}

      {/* Subscribers section */}
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: "16px", fontWeight: "700", color: "#111827" }}>Subscribed Users</div>
            <div style={{ fontSize: "13px", color: "#6B7280", marginTop: "2px" }}>
              Click "Visits" on any row to expand visit history
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                background: "#F3F4F6",
                borderRadius: "8px",
                padding: "6px 14px",
                fontSize: "13px",
                fontWeight: "700",
                color: "#374151",
              }}
            >
              {subsLoading ? "—" : subscribers.length} total
            </div>
            <div
              style={{
                background: "#E6F9F0",
                borderRadius: "8px",
                padding: "6px 14px",
                fontSize: "13px",
                fontWeight: "700",
                color: "#27AE60",
                border: "1px solid #27AE60",
              }}
            >
              {subsLoading ? "—" : subscribers.filter(u => (u.plan_status || u.status || "Active") === "Active").length} active
            </div>
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>User Name</th>
                <th style={thStyle}>Purchase Date</th>
                <th style={thStyle}>End Date</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Total Visits</th>
                <th style={thStyle}>Schedule Type</th>
                <th style={thStyle}>Frequency</th>
                <th style={thStyle}>Schedule Start</th>
                <th style={thStyle}>Schedule Days</th>
                <th style={thStyle}>Time Slot</th>
                <th style={thStyle}>{isClinicService ? "Assigned Doctor" : "Assigned Crew"}</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Billing</th>
                <th style={thStyle}>Visit History</th>
              </tr>
            </thead>
            <tbody>
              {subsLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].map(j => (
                        <td key={j} style={{ padding: "14px 16px" }}>
                          <Skeleton height="16px" marginBottom="0" />
                        </td>
                      ))}
                    </tr>
                  ))
                : subscribers.length === 0
                ? (
                  <tr>
                    <td
                      colSpan={13}
                      style={{ padding: "48px 24px", textAlign: "center", color: "#6B7280", fontSize: "14px" }}
                    >
                      No users have subscribed to this plan yet
                    </td>
                  </tr>
                )
                : subscribers.map((user, i) => (
                    <UserVisitsRow key={user.id || user.user_subscription_id || i} user={user} onPaymentRecorded={fetchSubscribers} />
                  ))
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionDetail;
